import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { Dialog, DialogContent } from '@mui/material';
import { isMobileView } from '../ReactiveRender';
import DateSelector from '../DateSelector';
import {
  IbmplexsansSemiBoldShark24px,
  IbmplexsansSemiBoldWhite16px,
} from '../../styledMixins';

export default function DatePickerPopup({
  isOpen,
  onClose,
  onSubmit,
  initialDate = dayjs().add(1, 'day'),
  minDate,
  maxDate,
  title = 'Select Date',
  description,
  dateType = 'date',
  containerElementId = 'dateContainer',
  validateDate = () => true,
}) {
  const [selectedDate, setSelectedDate] = useState(initialDate);

  useEffect(() => {
    if (initialDate) {
      setSelectedDate(initialDate);
    }
  }, [initialDate]);

  const handleDateChange = (newValue) => {
    setSelectedDate(newValue);
  };

  const handleSubmit = () => {
    if (!dayjs(selectedDate).isValid()) {
      return false;
    }

    if (!validateDate(selectedDate)) {
      return false;
    }

    onSubmit(selectedDate);

    onClose();
  };

  const dateSelectorFrame = (
    <DateSelector
      value={dayjs(selectedDate)}
      onChange={handleDateChange}
      minDate={minDate}
      maxDate={maxDate}
      type={dateType}
      id={containerElementId}
    />
  );

  const content = (
    <>
      <TitleContainer>
        <Icon src="/icons/clock.svg" alt="icon" />
        <Title>{title}</Title>
      </TitleContainer>
      <Line141 src="/img/line-14@2x.png" />
      {description && <TextContent>{description}</TextContent>}
      <TextContent>{dateSelectorFrame}</TextContent>
      <ButtonsContainer>
        <CancelButton onClick={onClose}>Cancel</CancelButton>
        <ActionButton onClick={handleSubmit}>Submit</ActionButton>
      </ButtonsContainer>
    </>
  );

  return isOpen ? (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogContent>
        {isMobileView() ? (
          <PopupContainerSmall>{content}</PopupContainerSmall>
        ) : (
          <PopupContainer>{content}</PopupContainer>
        )}
      </DialogContent>
    </Dialog>
  ) : null;
}

const Title = styled.div`
  display: flex;
  width: 277.333px;
  flex-direction: column;
  flex-shrink: 0;
  color: #505050;
  font-size: 16px;
  font-family: IBM Plex Sans;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`;

const ButtonsContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: flex-end;
  gap: 20px;
  margin-bottom: 20px;
  margin-right: 40px;
  padding-top: 30px;
`;

const Icon = styled.img`
  position: relative;
  min-width: 20px;
  height: 20px;
`;

const ActionButton = styled.div`
  ${IbmplexsansSemiBoldWhite16px}
  display: inline-flex;
  padding: 8px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 30px;
  border: 1px solid var(--light-mode-purple, #7200e0);
  background: var(--light-mode-purple, #7200e0);
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  &:hover {
    transform: scale(1.1);
  }
`;

const CancelButton = styled.div`
  color: #7200e0;
  border: none;
  font-weight :500;
   font-family: IBM Plex Sans;
  background-color: transparent;
  cursor: pointer;
  transition: transform 0.2s ease-in-out;

  &:hover {
    transform: scale(1.08);
  }
`;


const TextContent = styled.div`
  ${IbmplexsansSemiBoldShark24px}
  display: flex;
  width: 90%;
  flex-direction: column;
  flex-shrink: 0;
  align-items: flex-start;
  padding-top: 20px;
`;

const PopupContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  width: 500px;
  z-index: 1000;
  align-items: center;
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  border-radius: 12px;
  background: #fff;
  box-shadow: 0px 4px 16px 0px rgba(114, 0, 224, 0.1);
`;

const PopupContainerSmall = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-direction: column;
  align-items: center;
  position: fixed;
  width: 90%;
  top: 50%;
  left: 50%;
  z-index: 1000;
  transform: translate(-50%, -50%);
  border-radius: 12px;
  background: #fff;
  box-shadow: 0px 4px 16px 0px rgba(114, 0, 224, 0.1);
`;

const TitleContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  width: 100%;
  margin-left: 30px;
  gap: 10px;
  align-items: center;
  margin-top: 20px;
  padding-bottom: 10px;
`;

const Line141 = styled.img`
  position: relative;
  align-self: stretch;
  height: 1px;
  object-fit: cover;
`;